import type { InputHTMLAttributes, ReactNode } from "react";
import { cx } from "../utils/format";
import { AudienceTag, useShows, useTagShown, type Audience } from "./Audience";

/** the label, with the audience tag beside it when the row has one (outside the label, so the label's text stays the label) */
function RowLabel({ label, audience }: { label: string; audience?: Audience }) {
  const tagged = useTagShown(audience);
  if (!audience || !tagged) return <div className="ob-form-row__label">{label}</div>;
  return <div className="ob-form-row__head"><div className="ob-form-row__label">{label}</div><AudienceTag audience={audience} /></div>;
}

/** One setting: label and help on the left, the control on the right. `audience` tags who sees it; a `platform` row (a placeholder)
 *  renders only for a platform administrator. */
export function FormRow({ label, help, children, inline, stack, audience }: { label: string; help?: ReactNode; children: ReactNode; inline?: boolean; /** the control is a sentence of fields and sits below the text instead of beside it */ stack?: boolean; audience?: Audience }) {
  if (!useShows(audience)) return null;
  return (
    <div className={cx("ob-form-row", inline && "ob-form-row--inline", stack && "ob-form-row--stack")}>
      <div className="ob-form-row__text"><RowLabel label={label} audience={audience} />{help && <div className="ob-form-row__help">{help}</div>}</div>
      <div className="ob-form-row__control">{children}</div>
    </div>
  );
}

/** The one switch a page is about, in its own block above the groups it governs. `audience` tags who sees it. */
export function FormMaster({ label, help, audience, children }: { label: string; help?: ReactNode; audience?: Audience; children: ReactNode }) {
  if (!useShows(audience)) return null;
  return (
    <div className="ob-form__master">
      <div className="ob-form-row__text"><RowLabel label={label} audience={audience} />{help && <div className="ob-form-row__help">{help}</div>}</div>
      {children}
    </div>
  );
}

export function Input({ className, prefix, suffix, ...rest }: InputHTMLAttributes<HTMLInputElement> & { prefix?: string; suffix?: string }) {
  return (
    <span className={cx("ob-input", className)}>
      {prefix && <span className="ob-input__affix">{prefix}</span>}
      <input className="ob-input__field" {...rest} />
      {suffix && <span className="ob-input__affix">{suffix}</span>}
    </span>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <input type="checkbox" role="switch" className="ob-switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-label={label} />;
}

export function Range({ value, min, max, step, onChange, label, disabled }: { value: number; min: number; max: number; step: number; onChange: (v: number) => void; label: string; disabled?: boolean }) {
  const pct = ((value - min) / (max - min)) * 100; // the drawn track (mouse only) fills up to here
  return <input type="range" className="ob-range" value={value} min={min} max={max} step={step} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} aria-label={label} style={{ ["--pct" as string]: `${pct}%` }} />;
}

/** A titled group of rows. `disabled` greys the whole group and says why, so the settings stay readable while they are not in use.
 *  `audience` tags who sees it; a `platform` section (a placeholder) renders only for a platform administrator. The intro is a block, so
 *  it may hold a `HelpList` or a `More`. */
export function FormSection({ title, intro, disabled, toggle, action, audience, children }: { title: string; intro?: ReactNode; /** the rows are greyed and inert; the text does not change */ disabled?: boolean; /** a switch beside the heading that governs the rows below */ toggle?: ReactNode; /** a link or button at the heading's end (the WordPress card's "Download the plugin", 2026-10-13) */ action?: ReactNode; audience?: Audience; children: ReactNode }) {
  if (!useShows(audience)) return null;
  return (
    <section className={cx("ob-form__section", disabled && "ob-form__section--disabled")}>
      <div className="ob-form__head"><h2 className="ob-form__title">{title}</h2>{audience && <AudienceTag audience={audience} />}{toggle && <span className="ob-form__toggle">{toggle}</span>}{action && <span className="ob-form__action">{action}</span>}</div>
      {intro && <div className="ob-form__intro">{intro}</div>}
      {/* the rows are what is inert, not the heading's own switch, which is how they come back */}
      <div className="ob-form__rows" aria-disabled={disabled || undefined}>{children}</div>
    </section>
  );
}

export function Meter({ value, max, label }: { value: number; max: number; label?: string }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className={cx("ob-meter", pct >= 100 && "ob-meter--full")} role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max} aria-label={label}>
      <span className="ob-meter__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}
